import { Values, FormFields } from '../types'
import { getIsDateBlocked } from '../utils'
import DatepickerInput from '@components/Datepicker'
import { Field } from '@components/gassan-ui'
import { FormField } from '@generated'
import { useFormikContext } from 'formik'
import { useTranslation } from 'next-i18next'
import { FC } from 'react'

type EventSpecificFieldsProps = {
  fields: FormField[] | FormFields
  selectableDates: string[]
  selectableTimes: string[]
  maxPersons: number
  isNewApiStructure?: boolean
}

export const EventSpecificFields: FC<EventSpecificFieldsProps> = ({
  fields,
  selectableDates,
  selectableTimes,
  maxPersons,
  isNewApiStructure = false,
}) => {
  const { t } = useTranslation('forms')
  const { setFieldValue, values, setTouched } = useFormikContext<Values>()

  const getLabel = (fieldName: FormField, baseLabel: string): string => {
    if (!isNewApiStructure) {
      return baseLabel
    }

    const formFields = fields as FormFields
    const isMandatory = formFields.find((f) => f.type === fieldName)?.mandatory ?? false
    return isMandatory ? baseLabel : `${baseLabel} - ${t('optional', { ns: 'forms' })}`
  }

  const isFieldEnabled = (fieldName: FormField): boolean => {
    if (isNewApiStructure) {
      const formFields = fields as FormFields
      return !!formFields.find((f) => f.type === fieldName)
    } else {
      const inputFields = fields as FormField[]
      return inputFields.includes(fieldName)
    }
  }

  return (
    <>
      {isFieldEnabled(FormField.DietaryRequirements) && (
        <Field
          name="dietaryRequirements"
          label={getLabel(FormField.DietaryRequirements, t('labels.dietaryRequirements'))}
          field="textarea"
          gridColumn="span 12"
        />
      )}
      {isFieldEnabled(FormField.NumberOfPersons) && typeof maxPersons === 'number' && (
        <Field
          name="numberOfPersons"
          label={getLabel(FormField.NumberOfPersons, t('labels.numberOfPersons'))}
          field="select"
          placeholder=" "
          gridColumn="span 12"
        >
          <option value="" defaultChecked disabled>
            {t('labels.numberOfPersons')}
          </option>
          {[...Array.from(new Array(maxPersons))].map((_, i) => (
            <option key={i + 1} value={i + 1}>
              {i + 1}
            </option>
          ))}
        </Field>
      )}
      {isFieldEnabled(FormField.Date) && (
        <DatepickerInput
          isDateBlocked={getIsDateBlocked(selectableDates)}
          name="preferredDate"
          label={getLabel(FormField.Date, t('date', { ns: 'other' }))}
          value={values.date}
          onChange={(date) => {
            setFieldValue('date', date)
          }}
          onBlur={() => setTouched({ date: true })}
        />
      )}
      {isFieldEnabled(FormField.Time) && selectableTimes.length > 0 && (
        <Field
          name="time"
          label={getLabel(FormField.Time, t('labels.selectTime', { ns: 'forms' }))}
          field="select"
          placeholder=" "
          gridColumn="span 12"
        >
          <option value="" defaultChecked disabled>
            {t('labels.selectTime', { ns: 'forms' })}
          </option>
          {selectableTimes.map((time, i) => (
            <option key={i} value={time}>
              {time}
            </option>
          ))}
        </Field>
      )}
      {isFieldEnabled(FormField.HeardOfGassanVia) && (
        <Field
          name="heardOfGassanVia"
          label={getLabel(
            FormField.HeardOfGassanVia,
            t('labels.heardOfGassanVia', { ns: 'forms' }),
          )}
          field="input"
          gridColumn="span 12"
        />
      )}
    </>
  )
}
